/**
 * Contract bridge scoring for one deal (Chicago / duplicate-style: no part-score carry-over, no honours).
 *
 *  - Trick points per odd trick: clubs and diamonds 20, hearts and spades 30, no trump 40 for the first and 30 after;
 *    doubled ×2, redoubled ×4.
 *  - A made contract worth 100+ trick points earns a game bonus (300 non-vulnerable, 500 vulnerable); less is a
 *    part-score bonus of 50. Small slam (level 6) 500 / 750 and grand slam (level 7) 1000 / 1500 on top.
 *  - Making a doubled contract adds 50 ("for the insult"), redoubled 100.
 *  - Overtricks: undoubled they score trick value; doubled 100 (vulnerable 200); redoubled 200 (vulnerable 400).
 *  - Undertricks: undoubled 50 each (vulnerable 100). Doubled, non-vulnerable: 100, then 200 for the 2nd and 3rd and 300
 *    after; vulnerable: 200 for the first, 300 after. Redoubled doubles those.
 */
export type BridgeStrain = 'C' | 'D' | 'H' | 'S' | 'N';
export const BRIDGE_STRAINS: readonly BridgeStrain[] = ['C', 'D', 'H', 'S', 'N'];

export interface BridgeContract {
  level: number;
  strain: BridgeStrain;
  /** 0 = undoubled, 1 = doubled, 2 = redoubled. */
  doubled: 0 | 1 | 2;
}

export interface BridgeDealScore {
  /** Points for the declaring side (negative when the contract failed it is 0 and `defenders` scores instead). */
  declarer: number;
  defenders: number;
  made: boolean;
  /** Tricks taken above (or below, negative) the contract's requirement. */
  overUnder: number;
  breakdown: { label: string; points: number }[];
}

const perTrick = (strain: BridgeStrain): number => (strain === 'C' || strain === 'D' ? 20 : 30);

/** `tricks` is the number of tricks the declaring side took (0–13). */
export function bridgeScoreDeal(contract: BridgeContract, vulnerable: boolean, tricks: number): BridgeDealScore {
  const { level, strain, doubled } = contract;
  const needed = 6 + level;
  const mult = doubled === 0 ? 1 : doubled === 1 ? 2 : 4;
  const breakdown: { label: string; points: number }[] = [];

  if (tricks < needed) {
    const down = needed - tricks;
    let pts = 0;
    if (doubled === 0) pts = down * (vulnerable ? 100 : 50);
    else {
      for (let i = 1; i <= down; i++) {
        if (vulnerable) pts += i === 1 ? 200 : 300;
        else pts += i === 1 ? 100 : i <= 3 ? 200 : 300;
      }
      if (doubled === 2) pts *= 2;
    }
    breakdown.push({ label: 'undertricks', points: pts });
    return { declarer: 0, defenders: pts, made: false, overUnder: -down, breakdown };
  }

  const base = strain === 'N' ? 40 + 30 * (level - 1) : perTrick(strain) * level;
  const contractPoints = base * mult;
  breakdown.push({ label: 'contract', points: contractPoints });
  let total = contractPoints;

  const over = tricks - needed;
  if (over > 0) {
    const each = doubled === 0 ? perTrick(strain) : (vulnerable ? 200 : 100) * (doubled === 2 ? 2 : 1);
    breakdown.push({ label: 'overtricks', points: over * each });
    total += over * each;
  }
  if (doubled > 0) {
    const insult = doubled === 1 ? 50 : 100;
    breakdown.push({ label: 'insult', points: insult });
    total += insult;
  }
  if (contractPoints >= 100) {
    const game = vulnerable ? 500 : 300;
    breakdown.push({ label: 'game', points: game });
    total += game;
  } else {
    breakdown.push({ label: 'partScore', points: 50 });
    total += 50;
  }
  if (level === 6) {
    const slam = vulnerable ? 750 : 500;
    breakdown.push({ label: 'smallSlam', points: slam });
    total += slam;
  } else if (level === 7) {
    const slam = vulnerable ? 1500 : 1000;
    breakdown.push({ label: 'grandSlam', points: slam });
    total += slam;
  }
  return { declarer: total, defenders: 0, made: true, overUnder: over, breakdown };
}
